import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { useRouterState } from "@tanstack/react-router"
import { AppRoutes } from "@/shared/lib/routes"
import { cn } from "@/lib/utils"
import { trackEvent } from "../track"
import { ANALYTICS_EVENTS } from "../types"
import {
  type CookieConsentChoice,
  getCookieConsent,
  setCookieConsent,
} from "../cookie-consent"
import BackIcon from "../assets/back.svg?react"
import CookieIcon from "../assets/cookie.svg?react"
import SettingsIcon from "../assets/settings.svg?react"

const LEGAL_ROUTES = new Set<string>([AppRoutes.COOKIES, AppRoutes.TERMS, AppRoutes.PRIVACY])

const CHOICE_DETAILS = [
  {
    title: "Accept all",
    description:
      "Allow all cookies, including those used for analytics and personalized advertising.",
  },
  {
    title: "Necessary only",
    description: "Allow strictly necessary cookies, which include analytics, to ensure the site works properly and to understand how it is used.",
  },
  {
    title: "Decline all",
    description: "Decline non-essential cookies and continue browsing.",
  },
] as const

const cardClass =
  "fixed bottom-5 left-1/2 z-[10000001] flex max-h-[calc(100dvh-40px)] w-[calc(100%-40px)] max-w-[460px] -translate-x-1/2 flex-col gap-4 overflow-auto rounded-2xl bg-card-purple p-4 font-body text-purple-light shadow-[0_0_20px] shadow-dark-deep/40"

const bodyClass = "text-sm leading-normal text-purple-light/60"

const acceptClass =
  "flex h-[42px] items-center justify-center rounded-lg bg-main-purple px-6 text-sm font-medium text-white transition-none hover:bg-main-purple/90"

function CookiePolicyLink({ children }: { children: string }) {
  return (
    <a
      href={AppRoutes.COOKIES}
      target="_blank"
      rel="noopener noreferrer"
      className="underline underline-offset-2"
    >
      {children}
    </a>
  )
}

function CookieConsentCard({
  onChoose,
}: {
  onChoose: (choice: CookieConsentChoice) => void
}) {
  const [screen, setScreen] = useState<"summary" | "choices">("summary")

  if (screen === "choices") {
    return (
      <div
        data-slot="cookie-consent"
        className={cardClass}
        role="dialog"
        aria-modal="true"
        aria-label="Your cookie choices"
      >
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                aria-label="Back"
                onClick={() => setScreen("summary")}
                className="flex size-7 shrink-0 items-center justify-center text-purple-light"
              >
                <BackIcon className="size-5" />
              </button>
              <h2 className="flex-1 text-xl font-medium leading-normal text-white">
                Your cookie choices
              </h2>
            </div>
            <p className={bodyClass}>
              You can choose whether to accept or decline the cookies described below before
              continuing.
            </p>
          </div>

          <div className="flex flex-col gap-2 rounded-2xl bg-white/5 p-3">
            {CHOICE_DETAILS.map((choice, index) => (
              <div key={choice.title}>
                {index > 0 && <div className="mb-2 h-px bg-white/10" />}
                <p className="text-sm font-medium leading-normal text-white">{choice.title}</p>
                <p className={bodyClass}>{choice.description}</p>
              </div>
            ))}
          </div>

          <p className={bodyClass}>
            <CookiePolicyLink>Read more about cookies</CookiePolicyLink>
          </p>
        </div>

        <div className="flex w-full flex-col gap-1">
          <button type="button" onClick={() => onChoose("all")} className={cn(acceptClass, "w-full")}>
            Accept all
          </button>
          <button type="button" onClick={() => onChoose("necessary")} className={cn(acceptClass, "w-full")}>
            Necessary only
          </button>
          <button
            type="button"
            onClick={() => onChoose("declined")}
            className="flex h-[42px] w-full items-center justify-center rounded-lg bg-white/10 px-6 text-sm font-medium text-white transition-none hover:bg-white/15"
          >
            Decline all
          </button>
        </div>
      </div>
    )
  }

  return (
    <div
      data-slot="cookie-consent"
      className={cardClass}
      role="dialog"
      aria-modal="true"
      aria-label="This website uses cookies"
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-1.5">
          <CookieIcon className="size-7 shrink-0 text-purple-light" />
          <h2 className="flex-1 text-xl font-medium leading-normal text-white">
            This website uses cookies
          </h2>
        </div>
        <p className={bodyClass}>
          We use our own and third-party cookies to analyse our services and show ads related to
          your preferences, according to profiling based on your browsing habits (for example,
          websites visited). <CookiePolicyLink>Read more</CookiePolicyLink>
        </p>
      </div>

      <div className="flex w-full items-center gap-2">
        <button type="button" onClick={() => onChoose("all")} className={cn(acceptClass, "min-w-0 flex-1")}>
          Accept all
        </button>
        <button
          type="button"
          aria-label="Cookie settings"
          onClick={() => setScreen("choices")}
          className="flex size-[42px] shrink-0 items-center justify-center rounded-lg border border-purple-light/20 bg-transparent p-0 text-white transition-none"
        >
          <SettingsIcon className="size-6" />
        </button>
      </div>
    </div>
  )
}

export function CookieConsent() {
  const [visible, setVisible] = useState(() => getCookieConsent() === null)
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const shown = visible && !LEGAL_ROUTES.has(pathname)

  const choose = (choice: CookieConsentChoice) => {
    setCookieConsent(choice)
    setVisible(false)

    if (choice === "declined") return

    trackEvent(ANALYTICS_EVENTS.COOKIE_CONSENT, { metadata: { choice } })
  }

  useEffect(() => {
    if (!shown) return

    const root = document.getElementById("root")
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    root?.setAttribute("inert", "")

    return () => {
      document.body.style.overflow = previousOverflow
      root?.removeAttribute("inert")
    }
  }, [shown])

  if (!shown) return null

  return createPortal(
    <>
      <div aria-hidden className="fixed inset-0 z-[10000000] touch-none" />
      <CookieConsentCard onChoose={choose} />
    </>,
    document.body,
  )
}
